import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams } from 'react-router-dom';
import { FaCalendarAlt, FaUser, FaSearch, FaArrowRight, FaFilter, FaTimes } from 'react-icons/fa';
import { 
  blogPosts, 
  blogCategories, 
  getPostsByCategory, 
  searchPosts, 
  getCategoryName 
} from '../data/blogData';
import { useApiData } from '../hooks/useApiData';
import { fetchBlogs, fetchBlogCategories, fetchImage } from '../repository/api';
import { formatExcerpt, formatBlogContent } from '../utils/textFormatter';
import LoadingSpinner from '../components/LoadingSpinner';

const CategoryBlogPage = ({ match }) => {
  // Get the category ID from the URL parameters
  const { category } = useParams();
  const [categoryId, setCategoryId] = useState(category);
  
  // Update categoryId when URL parameter changes
  useEffect(() => {
    setCategoryId(category);
  }, [category]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 9;

  // Fetch blogs from API based on category
  const { data: blogsData, loading: blogsLoading, error: blogsError } = useApiData(() => 
    fetchBlogs({ 
      per_page: 50, 
      category: categoryId,
      sort_by: 'created_at', 
      sort_order: 'desc' 
    }), [categoryId]
  );
  
  // Fetch categories from API
  const { data: categoriesData, loading: categoriesLoading, error: categoriesError } = useApiData(fetchBlogCategories);

  // Transform API data to match component structure
  const transformApiData = (apiData) => {
    const isDebug = process.env.NODE_ENV === 'development';
    if (isDebug) console.log('CategoryBlogPage API Data received:', apiData);
    
    if (!apiData || !apiData.blogs || !Array.isArray(apiData.blogs) || apiData.blogs.length === 0) {
      if (isDebug) console.log('No valid blogs data found, using default data');
      return [];
    }
    
    return apiData.blogs.map((blog) => ({
      id: blog.id,
      title: blog.title || 'Untitled Blog Post',
      excerpt: formatExcerpt(blog.paragraph_explanation || 'No description available'),
      category: blog.category?.toLowerCase() || 'general',
      date: new Date(blog.created_at).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      author: blog.author_name || 'Anonymous',
      authorImage: null,
      authorBio: '',
      image: blog.image_path ? fetchImage(blog.image_path) : blogPosts[0].image,
      featured: false,
      tags: blog.tags || [],
      content: blog.sections ? formatBlogContent(blog.sections) : ''
    }));
  };

  // Transform categories data
  const transformCategoriesData = (apiData) => {
    if (!apiData || !apiData.categories || !Array.isArray(apiData.categories)) {
      return blogCategories;
    }
    
    return [
      { id: 'all', name: 'All Posts' },
      ...apiData.categories.map(category => ({
        id: category.toLowerCase().replace(/\s+/g, '-'),
        name: category
      }))
    ];
  };

  // Use API data or fallback to default
  const allBlogs = useMemo(() => {
    if (blogsData && !blogsLoading && !blogsError) {
      const transformed = transformApiData(blogsData);
      return transformed.length > 0 ? transformed : getPostsByCategory(categoryId);
    }
    return getPostsByCategory(categoryId);
  }, [blogsData, blogsLoading, blogsError, categoryId]);

  const availableCategories = useMemo(() => {
    if (categoriesData && !categoriesLoading && !categoriesError) {
      return transformCategoriesData(categoriesData);
    }
    return blogCategories;
  }, [categoriesData, categoriesLoading, categoriesError]);

  // Filter posts by search query
  useEffect(() => {
    let filtered = allBlogs;
    
    // Apply search filter if there's a query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(query) || 
        post.excerpt.toLowerCase().includes(query) ||
        post.category.toLowerCase().includes(query) ||
        post.author.toLowerCase().includes(query) ||
        post.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    setFilteredPosts(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchQuery, allBlogs]);

  // Get current category name (from API or fallback)
  const categoryName = useMemo(() => {
    const apiCategory = availableCategories.find(cat => cat.id === categoryId);
    return apiCategory ? apiCategory.name : getCategoryName(categoryId);
  }, [availableCategories, categoryId]);

  // Debug logging (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('CategoryBlogPage component state:', {
        categoryId,
        blogsLoading,
        blogsError,
        categoriesLoading,
        categoriesError,
        hasApiData: !!blogsData,
        hasCategoriesData: !!categoriesData,
        allBlogsCount: allBlogs.length,
        filteredPostsCount: filteredPosts.length,
        availableCategoriesCount: availableCategories.length,
        categoryName
      });
    }
  }, [categoryId, blogsLoading, blogsError, categoriesLoading, categoriesError, blogsData, categoriesData, allBlogs.length, filteredPosts.length, availableCategories.length, categoryName]);

  // Show error state if API call failed (but continue with fallback data)
  if (blogsError || categoriesError) {
    const isDebug = process.env.NODE_ENV === 'development';
    if (isDebug) {
      console.error('CategoryBlogPage API errors:', { blogsError, categoriesError });
      console.log('Using fallback blog data due to API errors');
    }
  }
  
  // Pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <PageWrapper>
      <PageHeader style={{background: getCategoryGradient(categoryId)}}>
        <Container>
          <BreadcrumbNav>
            <BreadcrumbLink href="/">Home</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink href="/blog">Blog</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbCurrent>{categoryName}</BreadcrumbCurrent>
          </BreadcrumbNav>
          <PageTitle>{categoryName}</PageTitle>
          <PageDescription>
            {getCategoryDescription(categoryId)}
          </PageDescription>
          
          <MobileFilterButton onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? <FaTimes /> : <FaFilter />}
            {showFilters ? 'Close' : 'Filters'}
          </MobileFilterButton>
        </Container>
      </PageHeader>

      <Container>
        <ContentWrapper>
          <Sidebar show={showFilters}>
            <SidebarWidget>
              <WidgetTitle>Categories</WidgetTitle>
              <CategoryList>
                {availableCategories.map(category => (
                  <CategoryItem key={category.id} isActive={category.id === categoryId}>
                    <CategoryLink 
                      href={`/blog/category/${category.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setCategoryId(category.id);
                        setShowFilters(false);
                      }}
                    >
                      {category.name}
                      {category.id === categoryId && <ActiveIndicator />}
                    </CategoryLink>
                  </CategoryItem>
                ))}
              </CategoryList>
            </SidebarWidget>
            
            
            
            <SidebarWidget>
              <WidgetTitle>Subscribe</WidgetTitle>
              <SubscribeForm>
                <p>Get the latest articles directly to your inbox</p>
                <SubscribeInput type="email" placeholder="Your email address" />
                <SubscribeButton>Subscribe</SubscribeButton>
              </SubscribeForm>
            </SidebarWidget>
          </Sidebar>
          
          <MainContent>
            <SearchBox>
              <SearchInput 
                type="text" 
                placeholder={`Search in ${categoryName}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <SearchIcon><FaSearch /></SearchIcon>
              {searchQuery && (
                <ClearButton onClick={() => setSearchQuery('')}>
                  <FaTimes />
                </ClearButton>
              )}
            </SearchBox>
            
            {blogsLoading ? (
              <LoadingWrapper>
                <LoadingSpinner 
                  size="medium" 
                  text="Loading articles..." 
                  overlay={false}
                  showLogo={true}
                />
              </LoadingWrapper>
            ) : currentPosts.length > 0 ? (
              <>
                <BlogGrid>
                  {currentPosts.map((post) => (
                    <BlogPost key={post.id}>
                      <BlogPostImageWrapper>
                        <BlogPostImage src={post.image} alt={post.title} />
                      </BlogPostImageWrapper>
                      
                      <BlogPostContent>
                        <BlogPostMeta>
                          <MetaItem>
                            <FaCalendarAlt />
                            <span>{post.date}</span>
                          </MetaItem>
                          <MetaItem>
                            <FaUser />
                            <span>{post.author}</span>
                          </MetaItem>
                        </BlogPostMeta>
                        
                        <BlogPostTitle href={`/blog/${post.id}`}>
                          {post.title}
                        </BlogPostTitle>
                        <BlogPostExcerpt dangerouslySetInnerHTML={{ __html: post.excerpt }} />
                        
                        <ReadMoreLink href={`/blog/${post.id}`}>
                          Read more <FaArrowRight />
                        </ReadMoreLink>
                      </BlogPostContent>
                    </BlogPost>
                  ))}
                </BlogGrid>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <Pagination>
                    <PaginationButton 
                      onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </PaginationButton>
                    
                    <PaginationNumbers>
                      {[...Array(totalPages)].map((_, index) => (
                        <PaginationNumber 
                          key={index}
                          isActive={currentPage === index + 1}
                          onClick={() => paginate(index + 1)}
                        >
                          {index + 1}
                        </PaginationNumber>
                      ))}
                    </PaginationNumbers>
                    
                    <PaginationButton 
                      onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </PaginationButton>
                  </Pagination>
                )}
              </>
            ) : (
              <NoResultsMessage>
                No articles found matching your criteria. Please try a different search.
              </NoResultsMessage>
            )}
          </MainContent>
        </ContentWrapper>
      </Container>
    </PageWrapper>
  );
};

// Helper function to get category gradient
const getCategoryGradient = (categoryId) => {
  const gradients = {
    'sms': 'linear-gradient(135deg, #105090, #2090B0)',
    'ussd': 'linear-gradient(135deg, #155799, #159957)',
    'call-center': 'linear-gradient(135deg, #4568dc, #b06ab3)',
    'payments': 'linear-gradient(135deg, #3a7bd5, #3a6073)',
    'software': 'linear-gradient(135deg, #614385, #516395)'
  };
  return gradients[categoryId] || 'linear-gradient(135deg, #105090, #2090B0)';
};

// Helper function to get category description
const getCategoryDescription = (categoryId) => {
  const descriptions = {
    'sms': 'Explore our articles on bulk SMS solutions, implementation strategies, and success stories.',
    'ussd': 'Discover the power of USSD services for businesses across Africa and beyond.',
    'call-center': 'Learn about optimizing call center operations and enhancing customer experiences.',
    'payments': 'Stay updated on secure and efficient payment solutions for businesses of all sizes.',
    'software': 'Insights on custom software development, trends, and best practices.'
  };
  return descriptions[categoryId] || 'Browse our collection of informative articles and insights.';
};



export default CategoryBlogPage;

// === Animations ===
const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// === Styled Components ===
const PageWrapper = styled.div`
margin-top: 10vh;
  background-color: #f8f9fb;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
`;

const PageHeader = styled.div`
  background: linear-gradient(135deg, #105090, #2090B0);
  background-size: 200% auto;
  animation: ${gradientAnimation} 10s ease infinite;
  color: white;
  padding: 80px 0 60px;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    padding: 60px 0 40px;
    margin-bottom: 40px;
  }
`;

const BreadcrumbNav = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  font-size: 14px;
  flex-wrap: wrap;
`;

const BreadcrumbLink = styled.a`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  
  &:hover {
    color: white;
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 10px;
  color: rgba(255, 255, 255, 0.5);
`;

const BreadcrumbCurrent = styled.span`
  color: white;
  font-weight: 500;
`;

const PageTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 576px) {
    font-size: 30px;
  }
`;

const PageDescription = styled.p`
  font-size: 18px;
  max-width: 600px;
  margin: 0 auto;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const MobileFilterButton = styled.button`
  position: absolute;
  top: 120px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 30px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  backdrop-filter: blur(5px);
  display: none;
  
  @media (max-width: 992px) {
    display: flex;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 30px;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.aside`
  width: 280px;
  position: sticky;
  top: 30px;
  align-self: flex-start;
  
  @media (max-width: 992px) {
    width: 100%;
    position: static;
    display: ${props => props.show ? 'block' : 'none'};
  }
`;

const MainContent = styled.main`
  flex: 1;
`;

// Sidebar components
const SidebarWidget = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
  overflow: hidden;
`;

const WidgetTitle = styled.h3`
  background: linear-gradient(90deg, #105090, #2090B0);
  color: white;
  font-size: 18px;
  font-weight: 600;
  padding: 15px 20px;
  margin: 0;
`;

// Categories Widget
const CategoryList = styled.ul`
  list-style: none;
  padding: 15px 5px 15px 20px;
  margin: 0;
`;

const CategoryItem = styled.li`
  margin-bottom: 10px;
  position: relative;
  padding-left: ${props => props.isActive ? '15px' : '0'};
  transition: all 0.3s ease;
`;

const CategoryLink = styled.a`
  color: ${props => props.isActive ? '#2090B0' : '#444'};
  text-decoration: none;
  font-size: 15px;
  display: block;
  padding: 8px 0;
  transition: all 0.3s ease;
  font-weight: ${props => props.isActive ? '600' : '400'};
  
  &:hover {
    color: #2090B0;
  }
`;

const ActiveIndicator = styled.span`
  position: absolute;
  left: -15px;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  background: #2090B0;
  border-radius: 3px;
`;

// Tags Cloud
const TagsCloud = styled.div`
  padding: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const TagBadge = styled.a`
  background-color: #f0f2f5;
  color: #666;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #e0e2e5;
    color: #2090B0;
    transform: translateY(-2px);
  }
`;

// Subscribe Widget
const SubscribeForm = styled.form`
  padding: 20px;
  
  p {
    margin-top: 0;
    margin-bottom: 15px;
    font-size: 14px;
    color: #666;
  }
`;

const SubscribeInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 15px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #2090B0;
    box-shadow: 0 0 0 3px rgba(32, 144, 176, 0.1);
  }
`;

const SubscribeButton = styled.button`
  background: linear-gradient(90deg, #105090, #2090B0);
  background-size: 200% auto;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.3s ease;
  
  &:hover {
    background-position: right center;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

// Search box
const SearchBox = styled.div`
  position: relative;
  margin-bottom: 30px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 15px 20px;
  padding-right: 50px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
  
  &::placeholder {
    color: #aaa;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #2090B0;
  font-size: 16px;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 5px;
  
  &:hover {
    color: #2090B0;
  }
`;

// Blog Grid
const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogPost = styled.article`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
`;

const BlogPostImageWrapper = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
`;

const BlogPostImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${BlogPost}:hover & {
    transform: scale(1.05);
  }
`;

const BlogPostContent = styled.div`
  padding: 25px;
`;

const BlogPostMeta = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 12px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  color: #777;
  font-size: 14px;
  
  svg {
    margin-right: 6px;
    color: #2090B0;
  }
`;

const BlogPostTitle = styled.a`
  display: block;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
  line-height: 1.4;
  color: #105090;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    color: #2090B0;
  }
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const BlogPostExcerpt = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 15px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ReadMoreLink = styled.a`
  display: flex;
  align-items: center;
  color: #2090B0;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 6px;
    font-size: 12px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: #105090;
    
    svg {
      transform: translateX(3px);
    }
  }
`;

// Pagination
const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 50px;
  margin-bottom: 20px;
`;

const PaginationButton = styled.button`
  padding: 10px 20px;
  background: ${props => props.disabled ? '#e0e0e0' : 'white'};
  color: ${props => props.disabled ? '#999' : '#105090'};
  border: 1px solid #e0e0e0;
  border-radius: 30px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: ${props => props.disabled ? '#e0e0e0' : '#f8f8f8'};
    box-shadow: ${props => props.disabled ? 'none' : '0 5px 15px rgba(0, 0, 0, 0.05)'};
  }
`;

const PaginationNumbers = styled.div`
  display: flex;
  margin: 0 20px;
  flex-wrap: wrap;
  justify-content: center;
  gap: 5px;
`;

const PaginationNumber = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  margin: 0 5px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  background: ${props => props.isActive ? 'linear-gradient(90deg, #105090, #2090B0)' : 'white'};
  color: ${props => props.isActive ? 'white' : '#666'};
  box-shadow: ${props => props.isActive ? '0 5px 15px rgba(0, 0, 0, 0.1)' : '0 3px 10px rgba(0, 0, 0, 0.05)'};
  
  &:hover {
    transform: ${props => !props.isActive && 'translateY(-2px)'};
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 576px) {
    width: 32px;
    height: 32px;
    margin: 0 2px;
  }
`;

// Loading and error messages
const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
`;

const LoadingText = styled.p`
  color: #666;
  font-size: 16px;
`;

const NoResultsMessage = styled.div`
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 12px;
  margin: 20px 0;
  color: #666;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
`;